import { Switch } from "../ui/switch";
import { useFieldContext } from "./form-context";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { CircleAlert, Redo2 } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Button } from "../ui/button";
import { cn, sanitizeHtml } from "@/lib/utils";
import { OverridesButton } from "../overrides-button";

type FormSwitchProps = {
  id: string;
  label?: ReactNode;
  description?: string;
  htmlContent?: string;
  defaultSystemValue?: string;
  hasOverriddenValue?: boolean;
  openOverridesModal?: () => void;
};

export function FormSwitch({
  id,
  label,
  description,
  htmlContent,
  defaultSystemValue,
  hasOverriddenValue,
  openOverridesModal,
}: FormSwitchProps) {
  const field = useFieldContext<any>();
  const value = field.state.value;
  const [overwrittenValueBoolean, setOverwrittenValueBoolean] = useState<boolean>(false);
  useEffect(() => {
    if (Number.isNaN(Number(defaultSystemValue))) return;
    setOverwrittenValueBoolean(Number(defaultSystemValue) ? true : false);
  }, [defaultSystemValue]);

  const toBoolean = (v: unknown): boolean => {
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v !== 0;
    if (typeof v === "string") {
      const normalized = v.trim().toLowerCase();
      if (normalized === "1" || normalized === "true") return true;
      if (normalized === "0" || normalized === "false" || normalized === "") return false;
    }
    if (v === null || v === undefined) return false;
    return Boolean(v);
  };

  let isChecked: boolean;
  if (Array.isArray(value)) {
    isChecked = value.includes(id);
  } else {
    isChecked = toBoolean(value);
  }
  const sanitizedHtml = htmlContent ? sanitizeHtml(htmlContent) : null;
  const sanitizedHtmlLabel = typeof label === "string" ? sanitizeHtml(label) : null;

  const handleToggle = (checked: boolean) => {
    if (typeof value === "boolean") {
      field.handleChange(checked);
    } else if (Array.isArray(value)) {
      if (checked) {
        if (!value.includes(id)) {
          field.handleChange([...value, id]);
        }
      } else {
        field.handleChange(value.filter((item) => item !== id));
      }
    } else if (typeof value === "string" || typeof value === "number" || value === undefined) {
      field.handleChange(checked ? "1" : "0");
    } else if (value === null) {
      field.handleChange(checked ? true : false);
    } else {
      field.handleChange(checked ? id : "");
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center gap-3">
        <Switch id={id} checked={isChecked} onCheckedChange={handleToggle} />
        <div className="flex items-center gap-2">
          {label &&
            (typeof label === "string" ? (
              <div
                className="text-sm leading-none font-medium"
                dangerouslySetInnerHTML={{ __html: sanitizedHtmlLabel || "" }}
              />
            ) : (
              <div className="text-sm leading-none font-medium">{label}</div>
            ))}
          {defaultSystemValue !== undefined && (
            <Tooltip>
              <TooltipTrigger asChild>
                <CircleAlert
                  className={cn(
                    "w-4 h-4 text-orange-500",
                    value === defaultSystemValue && "invisible pointer-events-none"
                  )}
                />
              </TooltipTrigger>
              <TooltipContent>
                <p>This value overwrites the default system value: {overwrittenValueBoolean ? "True" : "False"}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        {defaultSystemValue !== undefined && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "text-orange-500 pb-0 pt-0 mr-auto",
                  value === defaultSystemValue && "invisible pointer-events-none"
                )}
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  field?.setValue(defaultSystemValue ?? "");
                }}
              >
                <Redo2 className="w-2 h-2" />
                Reset to default
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Reset the value to the default system value: {overwrittenValueBoolean ? "True" : "False"}</p>
            </TooltipContent>
          </Tooltip>
        )}
        {hasOverriddenValue && <OverridesButton onClick={openOverridesModal!} />}
      </div>
      {description && <div className="text-xs form-description">{description}</div>}
      {htmlContent && (
        <div className="text-xs form-description" dangerouslySetInnerHTML={{ __html: sanitizedHtml || "" }} />
      )}
    </div>
  );
}
